import React, { useState } from "react"
import Layout from "../../components/Layout"
import PasswordRequirements from "../../components/PasswordRequirements"
import { THEME } from "../../config/theme"
import { useAuth } from "../../context/AuthContext"
import { updateEmail, updatePassword } from "../../api/users"
import { isPasswordValid } from "../../utils/password"
import { formatTimestamp } from "../../utils/format"
import { formatRoleName } from "../../utils/roleName"

// ---------------------------------------------------------------------------
// Styles (shared across cards)
// ---------------------------------------------------------------------------

const cardStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.lg,
  padding: "28px",
  marginBottom: "20px",
  fontFamily: THEME.fonts.primary,
}

const cardTitle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: "20px",
}

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: "6px",
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: "40px",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: "0 12px",
  fontSize: "14px",
  fontFamily: THEME.fonts.primary,
  color: THEME.colors.neutral.light.textPrimary,
  boxSizing: "border-box",
  marginBottom: "14px",
  outline: "none",
}

const readOnlyInput: React.CSSProperties = {
  ...inputStyle,
  background: THEME.colors.neutral.light.background,
  color: THEME.colors.neutral.light.textSecondary,
  cursor: "default",
}

const btnPrimary = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  fontWeight: 500,
  fontSize: "14px",
  border: "none",
  borderRadius: THEME.borderRadius.md,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: THEME.fonts.primary,
  transition: "opacity 0.15s",
})

const successBanner: React.CSSProperties = {
  background: "rgba(5, 150, 105, 0.08)",
  color: THEME.colors.semantic.success,
  border: "0.5px solid rgba(5, 150, 105, 0.3)",
  padding: "10px 14px",
  borderRadius: THEME.borderRadius.md,
  fontSize: "13px",
  marginTop: "12px",
}

const errorBanner: React.CSSProperties = {
  background: "rgba(220, 38, 38, 0.08)",
  color: THEME.colors.semantic.danger,
  border: "0.5px solid rgba(220, 38, 38, 0.3)",
  padding: "10px 14px",
  borderRadius: THEME.borderRadius.md,
  fontSize: "13px",
  marginTop: "12px",
}

const hintText: React.CSSProperties = {
  fontSize: "12px",
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: "-8px",
  marginBottom: "14px",
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MyAccountPage: React.FC = () => {
  const { user, refreshUser, adoptToken } = useAuth()

  // Email form
  const [emailCurrentPw, setEmailCurrentPw] = useState("")
  const [newEmail, setNewEmail] = useState("")
  const [confirmEmail, setConfirmEmail] = useState("")
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)

  // Password form
  const [pwCurrentPw, setPwCurrentPw] = useState("")
  const [newPw, setNewPw] = useState("")
  const [confirmPw, setConfirmPw] = useState("")
  const [pwSubmitting, setPwSubmitting] = useState(false)
  const [pwSuccess, setPwSuccess] = useState<string | null>(null)
  const [pwError, setPwError] = useState<string | null>(null)

  // -------------------------------------------------------------------------
  // Email submit
  // -------------------------------------------------------------------------

  const emailFormValid =
    emailCurrentPw.length > 0 &&
    newEmail.length > 0 &&
    newEmail === confirmEmail &&
    !emailSubmitting

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setEmailError(null)
    setEmailSuccess(null)
    setEmailSubmitting(true)
    try {
      await updateEmail(emailCurrentPw, newEmail)
      setEmailSuccess("Email updated successfully")
      setEmailCurrentPw("")
      setNewEmail("")
      setConfirmEmail("")
      await refreshUser()
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : "Failed to update email")
    } finally {
      setEmailSubmitting(false)
    }
  }

  // -------------------------------------------------------------------------
  // Password submit
  // -------------------------------------------------------------------------

  const pwFormValid =
    pwCurrentPw.length > 0 &&
    isPasswordValid(newPw) &&
    newPw === confirmPw &&
    !pwSubmitting

  const handlePwSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwError(null)
    setPwSuccess(null)
    setPwSubmitting(true)
    try {
      // The old login stopped working with the old password; keep the new one.
      const newToken = await updatePassword(pwCurrentPw, newPw)
      if (newToken) adoptToken(newToken)
      setPwSuccess("Password updated successfully")
      setPwCurrentPw("")
      setNewPw("")
      setConfirmPw("")
    } catch (err) {
      setPwError(err instanceof Error ? err.message : "Failed to update password")
    } finally {
      setPwSubmitting(false)
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Layout>
      <div
        style={{
          fontSize: "24px",
          fontWeight: 500,
          letterSpacing: "-0.5px",
          color: THEME.colors.neutral.light.textPrimary,
          marginBottom: "24px",
        }}
      >
        My Account
      </div>

      {/* Card 1 — Account Info */}
      <div style={cardStyle}>
        <div style={cardTitle}>Account Info</div>

        <label style={labelStyle}>Username</label>
        <input
          style={readOnlyInput}
          value={user?.username ?? ""}
          readOnly
          tabIndex={-1}
        />
        <div style={hintText}>Contact an admin to change your username</div>

        <label style={labelStyle}>Email</label>
        <input
          style={readOnlyInput}
          value={user?.email ?? "—"}
          readOnly
          tabIndex={-1}
        />

        <label style={labelStyle}>Role</label>
        <input
          style={readOnlyInput}
          value={user?.role ? formatRoleName(user.role.name) : "No role assigned"}
          readOnly
          tabIndex={-1}
        />

        <label style={labelStyle}>Account created</label>
        <input
          style={readOnlyInput}
          value={formatTimestamp((user as any)?.created_at)}
          readOnly
          tabIndex={-1}
        />
      </div>

      {/* Card 2 — Change Email */}
      <div style={cardStyle}>
        <div style={cardTitle}>Change Email</div>
        <form onSubmit={handleEmailSubmit}>
          <label style={labelStyle}>Current password</label>
          <input
            style={inputStyle}
            type="password"
            autoComplete="current-password"
            value={emailCurrentPw}
            onChange={(e) => setEmailCurrentPw(e.target.value)}
          />

          <label style={labelStyle}>New email</label>
          <input
            style={inputStyle}
            type="email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />

          <label style={labelStyle}>Confirm new email</label>
          <input
            style={inputStyle}
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
          />
          {confirmEmail.length > 0 && confirmEmail !== newEmail && (
            <div style={{ ...errorBanner, marginTop: "-6px", marginBottom: "12px" }}>
              Emails do not match
            </div>
          )}

          <button type="submit" disabled={!emailFormValid} style={btnPrimary(!emailFormValid)}>
            {emailSubmitting ? "Saving..." : "Update Email"}
          </button>

          {emailSuccess && <div style={successBanner}>{emailSuccess}</div>}
          {emailError && <div style={errorBanner}>{emailError}</div>}
        </form>
      </div>

      {/* Card 3 — Change Password */}
      <div style={cardStyle}>
        <div style={cardTitle}>Change Password</div>
        <form onSubmit={handlePwSubmit}>
          <label style={labelStyle}>Current password</label>
          <input
            style={inputStyle}
            type="password"
            autoComplete="current-password"
            value={pwCurrentPw}
            onChange={(e) => setPwCurrentPw(e.target.value)}
          />

          <label style={labelStyle}>New password</label>
          <input
            style={inputStyle}
            type="password"
            autoComplete="new-password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />

          <label style={labelStyle}>Confirm new password</label>
          <input
            style={inputStyle}
            type="password"
            autoComplete="new-password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />
          {confirmPw.length > 0 && confirmPw !== newPw && (
            <div style={{ ...errorBanner, marginTop: "-6px", marginBottom: "12px" }}>
              Passwords do not match
            </div>
          )}

          <PasswordRequirements password={newPw} />

          <button type="submit" disabled={!pwFormValid} style={btnPrimary(!pwFormValid)}>
            {pwSubmitting ? "Saving..." : "Update Password"}
          </button>

          {pwSuccess && <div style={successBanner}>{pwSuccess}</div>}
          {pwError && <div style={errorBanner}>{pwError}</div>}
        </form>
      </div>
    </Layout>
  )
}

export default MyAccountPage
